import { Flags } from '@fullstory/client-api';
import { intl } from '@fullstory/intl';
import { Card } from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { getDashboardPath } from '@fullstory/routes';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../types';
import { int64IDs, makeUseAccess, makeUseCreateAccess } from '../../utils/accessControl';
import { useRequestState } from '../../utils/hooks';
import { getOrgId } from '../fullstory/selectors';
import { CardRequestActions, DashboardRequestActions } from './actions';

import { dashboardsById } from './selectors';

const useDashboardsAccess = makeUseAccess(
  DashboardRequestActions.getAccess,
  (state: AppState) => state.dashboards.access,
);

export const useDashboardAccess = (id: int64, orgId: string, flags: Flags) => {
  const stableIds = useMemo(() => (id ? int64IDs([id]) : undefined), [id]);
  const { data } = useDashboardsAccess(stableIds, orgId, flags);
  return data?.access?.[id as string];
};

export const useDashboardCreateAccess = makeUseCreateAccess(
  DashboardRequestActions.getAccess,
  (state: AppState) => state.dashboards.access,
);

export const useDashboardsRequestState = () =>
  useRequestState(state => state.dashboards.dashboards);

export const useFetchAllDashboards = (orgId: string, email: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const flags = useFlags();

  const fetch = useCallback(() => {
    dispatch(
      DashboardRequestActions.fetchAllDashboard(
        { orgId, email, omitSeatSettings: false },
        email,
        flags,
      ),
    );
  }, [dispatch, orgId, email, flags]);

  const retry = useCallback(() => {
    dispatch(
      DashboardRequestActions.fetchAllDashboard(
        { orgId, email, omitSeatSettings: false },
        email,
        flags,
        true,
      ),
    );
  }, [dispatch, orgId, email, flags]);

  useEffect(() => fetch(), [fetch]);
  return {
    fetch,
    retry,
  };
};

export const useAllSavedDashboards = () => {
  const requestStateStatus = useRequestState((state: AppState) => state.dashboards.dashboards);

  return {
    ...requestStateStatus,
    byId: useSelector(dashboardsById),
  };
};

export const useDashboardActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();

  const markViewed = useCallback(
    (dashboardId: int64) =>
      dispatch(DashboardRequestActions.markDashboardViewed({ orgId, dashboardId }, flags)),
    [dispatch, flags, orgId],
  );

  const star = useCallback(
    async (dashboardId: int64) => {
      try {
        await dispatch(
          DashboardRequestActions.starDashboard({ orgId, dashboardId, starred: true }, flags),
        );
      } catch (e) {
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to star Dashboard', id: 'LuW/Pp' }),
        );
      }
    },
    [dispatch, notifications, orgId, flags],
  );

  const unstar = useCallback(
    async (dashboardId: int64) => {
      try {
        await dispatch(
          DashboardRequestActions.starDashboard({ orgId, dashboardId, starred: false }, flags),
        );
      } catch (e) {
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to unstar Dashboard', id: 'AJ5rza' }),
        );
      }
    },
    [dispatch, notifications, orgId, flags],
  );

  const hardDelete = useCallback(
    async (dashboardId: int64) => {
      try {
        const response = await dispatch(
          DashboardRequestActions.deleteDashboard(dashboardId, orgId, flags),
        );
        notifications?.success(
          intl.formatMessage({ defaultMessage: 'Successfully deleted Dashboard', id: 'Yw61CU' }),
        );
        return response;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to delete Dashboard', id: '4ctSBv' }),
        );
        return null;
      }
    },
    [dispatch, notifications, orgId, flags],
  );

  return {
    markViewed,
    star,
    unstar,
    hardDelete,
  };
};

export const useDashboardCardActions = (dashboardOverlayHash?: string) => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();

  const create = useCallback(
    async (dashboardId: int64, card: Card, insertAt: int64 | null) => {
      try {
        const response = await dispatch(
          CardRequestActions.createCard(
            {
              orgId,
              dashboardId,
              card,
              insertAt,
            },
            flags,
          ),
        );
        notifications.successLink(
          intl.formatMessage({
            defaultMessage: 'This card has been added to your dashboard. ',
            id: 'LqQgQW',
          }),
          intl.formatMessage({ defaultMessage: 'See it now.', id: 'gzvnO9' }),
          getDashboardPath(
            orgId,
            dashboardId,
            undefined,
            dashboardOverlayHash,
            response?.card?.id as string,
          ),
        );
        return response;
      } catch (e) {
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to create dashboard card.', id: 'Pl6erK' }),
        );
        throw e;
      }
    },
    [dispatch, orgId, flags, notifications, dashboardOverlayHash],
  );

  return {
    create,
  };
};
